import React, { forwardRef } from "react"
import { LexicalText } from "@/schema"

import { Editor, EditorRefHandle } from "@/components/lexical"
import type { PlaceholderData } from "@/components/lexical/placeholders"
import { MentionDropdownPlugin, MentionRecord } from "@/components/lexical/plugins/mention-dropdown"
import PasteImagePlugin from "@/components/lexical/plugins/paste-image"

interface TaskDescriptionEditorProps {
  onChange?: (lexicalText: LexicalText) => void
  placeholder?: string
  initialContent?: LexicalText
  onMentionAdded?: (mention: MentionRecord) => void
  onMentionRemoved?: (mention: MentionRecord) => void
  styleOverrides?: React.CSSProperties | Record<`--${string}`, string>
  includeUserMentions?: boolean
  enablePlaceholders?: boolean
  placeholders?: PlaceholderData[]
  editable?: boolean
}

/**
 * Wrap in forwardRef so the parent can do "ref={descriptionEditorRef}" and call .focus().
 * We use `EditorRefHandle` here, matching the Editor's forwardRef type.
 */
const TaskDescriptionEditor = forwardRef<EditorRefHandle, TaskDescriptionEditorProps>(
  (
    {
      onChange,
      placeholder = "Add description...",
      initialContent,
      onMentionAdded,
      onMentionRemoved,
      styleOverrides,
      includeUserMentions = true,
      enablePlaceholders,
      placeholders,
      editable = true,
    },
    ref
  ) => {
    const descriptionEditorStyles = {
      "--lexical-editor-min-height": "50px",
      "--lexical-editor-max-height": "50vh",
      ...styleOverrides,
    }

    const handleChange = (lexicalText: LexicalText) => {
      onChange?.(lexicalText)
    }

    return (
      <Editor
        ref={ref}
        onChange={handleChange}
        initialContent={initialContent}
        startingPlaceholder={placeholder}
        disableHeadings={true}
        styleOverrides={descriptionEditorStyles}
        enablePlaceholders={enablePlaceholders}
        placeholders={placeholders}
        editable={editable}
      >
        <PasteImagePlugin />
        <MentionDropdownPlugin
          mentionableTypes={
            includeUserMentions
              ? ["user", "client", "prospect", "contact", "person", "business", "trust"]
              : ["client", "prospect", "contact", "person", "business", "trust"]
          }
          onMentionAdded={onMentionAdded}
          onMentionRemoved={onMentionRemoved}
        />
      </Editor>
    )
  }
)

TaskDescriptionEditor.displayName = "TaskDescriptionEditor"

export default TaskDescriptionEditor
